import axios from 'axios';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints } from '@/ts/store/Constants';
import CalendarEventData from '@/ts/CalendarEventData';
import AddressData from '@/ts/AddressData';
import { EventDataResponse, AttendingRequestData } from '@/ts/HococoInterfaces';
import ErrorResponse from '@/ts/ErrorResponse';
import EventAttendees from '@/ts/EventAttendees';

@Module({name: 'Events', namespaced: true})
export default class Events extends VuexModule {
    public calendarEvents: CalendarEventData[] = [];
    public calendarEventsMap: { [index: number]: number} = {};
    public activeEventId: number = null;
    public hasAccessToCalendar: boolean = false;
    public calendarAccessRequested: boolean = false;
    public deviceCalendarName: string = null;
    public deviceCalendars: any[] = null;
    public eventsMap: { [index: number]: number} = {};
    public eventIndex: number = 0;
    public eventId: number;
    public cachedEvent: CalendarEventData = null;
    public editEventFormVisible: boolean = false;
    public eventsSorted: boolean = false;
    public addresses: AddressData[] = [];
    public addressesMap: { [index: number]: number} = {};
    public activeEventMedia: any = null;
    public attendees: EventAttendees[] = [];

    @Mutation
    public RESET_EVENTS_STORE_STATE() {
        this.calendarEvents          = [];
        this.calendarEventsMap       = {};
        this.activeEventId           = null;
        this.hasAccessToCalendar     = false;
        this.calendarAccessRequested = false;
        this.deviceCalendarName      = null;
        this.deviceCalendars         = null;
        this.eventsMap               = {};
        this.eventIndex              = 0;
        this.eventId                 = null;
        this.cachedEvent             = null;
        this.editEventFormVisible    = false;
        this.eventsSorted            = false;
        this.addresses               = [];
        this.addressesMap            = {};
        this.activeEventMedia        = null;
        this.attendees               = [];
    }

    get attendingEvent() {
        if (this.activeEventId === null) {
            return false;
        }
        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]].attending;
    }

    get getEvent() {
        return (eventId: number) => {
            return this.calendarEvents[this.calendarEventsMap[eventId]];
        };
    }

    get allEvents() {
        return this.calendarEvents;
    }

    get allAddresses() {
        return this.addresses;
    }

    get editEventMenuVisible(): boolean {
        return this.editEventFormVisible;
    }

    get activeEvent() {
        if (this.activeEventId === null) {
            return null;
        }

        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]];
    }

    get getEventId() {
        if (this.activeEventId === null) {
            return null;
        }

        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]].id;
    }

    get getEventTitle() {
        if (this.activeEventId === null) {
            return null;
        }

        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]].title;
    }

    get getCreationDate() {
        if (this.activeEventId === null) {
            return null;
        }

        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]].creationDate;
    }

    get getEventStartTime() {
        if (this.activeEventId === null) {
            return null;
        }

        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]].eventStart;
    }

    get getEventEndTime() {
        if (this.activeEventId === null) {
            return null;
        }

        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]].eventEnd;
    }

    get getEventDescription() {
        if (this.activeEventId === null) {
            return null;
        }

        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]].body;
    }

    get getActiveEvent() {
        return this.calendarEvents[this.calendarEventsMap[this.activeEventId]];
    }

    get getActiveEventId() {
        if (this.activeEventId != null) {
            return this.activeEventId;
        }

        return null;
    }

    get getEventData() {
        return this.calendarEvents;
    }

    get getEventAddress() {
        if (this.activeEventId === null) {
            return null;
        }

        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            return null;
        }

        return event.address.street;
    }

    get getEventCity() {
        if (this.activeEventId === null) {
            return null;
        }

        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            return null;
        }

        return event.address.city;
    }

    get getEventZipCode() {
        if (this.activeEventId === null) {
            return null;
        }

        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            return null;
        }

        return event.address.zip;
    }

    get getEventCountry() {
        if (this.activeEventId === null) {
            return null;
        }

        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            return null;
        }

        return event.address.countryId;
    }

    get getEventRequiresResponse() {
        if (this.activeEventId === null) {
            return null;
        }

        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        return event.requiresResponse;
    }

    get getActiveEventMedia() {
        return this.activeEventMedia;
    }

    get getAttendees(): EventAttendees[] {
        return this.attendees;
    }

    @Action
    public INITIATE_EVENT() {
        store.commit(Mutations.INITIATE_NEW_EVENT);
    }

    @Action
    public FETCH_EVENTS() {
        axios.get(Endpoints.Notifications.EVENTS_LIST)
            .then((response) => {
                if (response.data !== null) {
                    store.commit(Mutations.ADD_EVENTS, response.data.data);

                    if (Events.state.hasAccessToCalendar) {
                        store.dispatch(Actions.SYNCHRONIZE_EVENTS_WITH_CALENDAR);
                    }
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            }).then(() => {
            });

        return Promise.resolve();
    }

    @Action
    public SYNCHRONIZE_EVENTS_WITH_CALENDAR() {
        const calendar = (window as any).plugins.calendar;
        const events = Events.state.calendarEvents;

        const calendarOptions = {
            calendarName: Events.state.deviceCalendarName,
        };

        const promiseFactories = [];
        const results: any[] = [];

        for (const event of events) {
            promiseFactories.push(() => {
                return new Promise(
                    (resolve, reject) => {
                        const startTime = event.startMoment.toDate();
                        const endTime = event.endMoment.toDate();
                        // const location = ''; // TODO: Add location info

                        calendar.findEventWithOptions(
                            event.title,
                            // location,
                            event.body,
                            event.address,
                            event.city,
                            event.zipCode,
                            event.countryId,
                            startTime,
                            endTime,
                            calendarOptions,
                            (result: any) => {
                                if (result.length === 0) {
                                    calendar.createEventWithOptions(
                                        event.title,
                                        // location,
                                        event.body,
                                        event.address,
                                        event.city,
                                        event.zipCode,
                                        event.countryId,
                                        startTime,
                                        endTime,
                                        calendarOptions,
                                        (success: any) => {
                                            resolve(null);
                                        },
                                        (failure: any): void => {
                                            reject(null);
                                        },
                                    );
                                } else {
                                    resolve(null);
                                }
                            },
                            (failure: any) => {
                                reject();
                            },
                        );
                    },
                );
            });
        }

        let promiseResult = Promise.resolve();

        promiseFactories.forEach((promiseFactory: any) => {
            promiseResult = promiseResult.then(promiseFactory);
            return;
        });
    }

    @Action
    public FETCH_ADMIN_EVENTS() {
        return axios.get(Endpoints.Admin.Events.EVENTS)
            .then((response) => {
                if (response.data !== null) {
                        store.commit(Mutations.ADD_EVENTS, response.data.data);
                    }
                })
                .catch((error) => {
                    if (error.response) {
                        const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                        store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    } else {
                        console.log(error);
                    }
                });
        }

    @Action
    public POST_EVENTS(eventData: any) {
        const requestPayload = new FormData();

        requestPayload.append('title', eventData.title);
        requestPayload.append('description', eventData.description);
        requestPayload.append('eventStart', eventData.eventStart);
        requestPayload.append('eventEnd', eventData.eventEnd);
        requestPayload.append('publishedAt', eventData.publishedAt);
        requestPayload.append('address', eventData.address ? JSON.stringify(eventData.address) : null);
        requestPayload.append('requiresResponse', eventData.requiresResponse);
        requestPayload.append('actionTarget', JSON.stringify(eventData.actionTarget));
        requestPayload.append('mainImage', eventData.mainImage);

        axios.post(Endpoints.Admin.Events.EVENTS, requestPayload, {
            headers: {
                'Content-Type': 'multipart-data',
            },
        })
            .then((response) => {
                store.commit(Mutations.ADD_EVENT, response.data.data);
                this.activeEventId = null;
            })
            .catch((error) => {
                this.activeEventId = null;

                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_EVENT(eventData: any) {
        const requestPayload = new FormData();

        requestPayload.append('id', eventData.id);
        requestPayload.append('title', eventData.title);
        requestPayload.append('description', eventData.description);
        requestPayload.append('eventStart', eventData.eventStart);
        requestPayload.append('eventEnd', eventData.eventEnd);
        requestPayload.append('publishedAt', eventData.publishedAt);
        requestPayload.append('address', eventData.address ? JSON.stringify(eventData.address) : null);
        requestPayload.append('requiresResponse', eventData.requiresResponse);
        requestPayload.append('mainImage', eventData.mainImage);
        requestPayload.append('mainImageRemoved', eventData.mainImageRemoved);
        requestPayload.append('_method', 'PATCH');

        axios.patch(Endpoints.Admin.Events.SINGLE_EVENT(eventData.id), requestPayload, {
            headers: {
                'Content-Type': 'multipart-data',
            },
        })
            .then((response) => {
                store.commit(Mutations.UPDATE_CACHED_EVENT, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public POST_USER_ATTENDING_CALENDAR_EVENT(data: AttendingRequestData) {
        const url = Endpoints.Notifications.POST_USER_ATTENDING_CALENDAR_EVENT(data.eventId);

        return axios.post(url, { attending: data.attending })
            .then((response) => {
                store.commit(Mutations.ATTENDING_CALENDAR_EVENT, data.attending);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_EVENT(data: any) {
        axios.delete(Endpoints.Admin.Events.SINGLE_EVENT(data.id))
            .then((response) => {
                if (response.data) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    store.commit(Mutations.DELETE_SINGLE_EVENT, data.id);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_EVENT_MEDIA(eventId: number) {
        axios.post(Endpoints.Admin.Events.EVENT_MEDIA(eventId))
            .then((response) => {
                store.commit(Mutations.SET_ACTIVE_EVENT_MEDIA, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_EVENT_ATTENDEES(data: any) {
        const url = data.type === 'admin'
            ? Endpoints.Admin.Events.EVENT_ATTENDEES(data.eventId)
            : Endpoints.Notifications.EVENT_USER_ATTENDEES(data.eventId);

        axios.get(url)
            .then((response) => {
                store.commit(Mutations.SET_EVENT_ATTENDEES, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public ATTENDING_CALENDAR_EVENT(value: boolean) {
        if (this.calendarEvents[this.calendarEventsMap[this.activeEventId]]) {
            this.calendarEvents[this.calendarEventsMap[this.activeEventId]].attending = value;
        }
    }

    @Mutation
    public REQUESTED_CALENDAR_ACCESS() {
        this.calendarAccessRequested = true;
    }

    @Mutation
    public ACCEPT_CALENDAR_ACCESS() {
        this.hasAccessToCalendar = true;
    }

    @Mutation
    public UPDATE_ACTIVE_EVENT(eventId: number) {
        this.activeEventId = eventId;
    }

    @Mutation
    public ADD_EVENT(event: EventDataResponse) {
        const arrayIndex = 0;

        if (!this.calendarEventsMap.hasOwnProperty(event.id)) {
            this.calendarEvents.unshift(new CalendarEventData(
                event.id,
                event.title,
                event.body,
                event.isImportant,
                event.startTime,
                event.endTime,
                event.recipients,
                event.attenders,
                event.createdAt,
                event.corporations,
                event.projects,
                event.buildings,
                event.properties,
                event.author,
                event.publishedAt,
                event.notificationId,
            ));

            this.calendarEventsMap[event.id] = arrayIndex;

            if (typeof event.hasMedia !== 'undefined' && event.hasMedia !== null) {
                this.calendarEvents[arrayIndex].hasMedia = event.hasMedia;
            }

            if (event.attending !== null) {
                this.calendarEvents[arrayIndex].attending = event.attending;
            }

            if (event.address !== null) {
                this.calendarEvents[arrayIndex].addAddressData(event.address);
            }

            if (event.requiresResponse) {
                this.calendarEvents[arrayIndex].requiresResponse = true;
            }
        }

        this.activeEventId = null;
        this.calendarEventsMap = {};

        for (const [i, evt] of this.calendarEvents.entries()) {
            this.calendarEventsMap[evt.id] = i;
        }
    }

    @Mutation
    public ADD_EVENTS(eventData: EventDataResponse[]): void {
        for (const event of eventData) {
            let arrayIndex = null;

            if (!this.calendarEventsMap.hasOwnProperty(event.id)) {
                arrayIndex = this.calendarEvents.push(new CalendarEventData(
                    event.id,
                    event.title,
                    event.body,
                    event.isImportant,
                    event.startTime,
                    event.endTime,
                    event.recipients,
                    event.attenders,
                    event.createdAt,
                    event.corporations,
                    event.projects,
                    event.buildings,
                    event.properties,
                    event.author,
                    event.publishedAt,
                    event.notificationId,
                    event.queueIsExecuted,
                )) - 1;

                this.calendarEventsMap[event.id] = arrayIndex;

                if (typeof event.hasMedia !== 'undefined' && event.hasMedia !== null) {
                    this.calendarEvents[arrayIndex].hasMedia = event.hasMedia;
                }

                if (event.attending !== null) {
                    this.calendarEvents[arrayIndex].attending = event.attending;
                }

                if (event.address !== null) {
                    this.calendarEvents[arrayIndex].addAddressData(event.address);
                }

                if (event.requiresResponse) {
                    this.calendarEvents[arrayIndex].requiresResponse = true;
                }
            }
        }
    }

    @Mutation
    public SET_DEVICE_CALENDAR_NAME(calendarName: string) {
        this.deviceCalendarName = calendarName;
    }

    @Mutation
    public SET_DEVICE_CALENDARS(calendars: any[]) {
        this.deviceCalendars = calendars;
    }

    @Mutation
    public ADD_EVENT_ADMIN(event: EventDataResponse) {
        let arrayIndex = null;

        if (!this.calendarEventsMap.hasOwnProperty(event.id)) {
            arrayIndex = this.calendarEvents.push(new CalendarEventData(
                event.id,
                event.title,
                event.body,
                // event.timeSpring,
                event.isImportant,
                event.startTime,
                event.endTime,
                event.createdAt,
                event.corporations,
                event.projects,
                event.buildings,
                event.properties,
                event.author,
                event.publishedAt,
                event.notificationId,
            )) - 1;

            this.calendarEventsMap[event.id] = arrayIndex;

            if (event.requiresResponse) {
                this.calendarEvents[arrayIndex].requiresResponse = true;
            }

            if (event.address !== null) {
                this.calendarEvents[arrayIndex].addAddressData(event.address);
            }
        }
    }

    @Mutation
    public DELETE_SINGLE_EVENT(id: any): void {
        this.calendarEvents =  this.calendarEvents.filter((item: any) => {
            return item.id !== id;
        });
    }

    @Mutation
    public UPDATE_EVENT_TITLE(value: string): void {
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].title = value;
    }

    @Mutation
    public UPDATE_EVENT_CREATION_DATE(value: string): void {
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].creationDate = value;
    }

    @Mutation
    public UPDATE_EVENT_START_TIME(value: string): void {
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].eventStart = value;
    }

    @Mutation
    public UPDATE_EVENT_END_TIME(value: string): void {
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].eventEnd = value;
    }

    @Mutation
    public UPDATE_EVENT_ADDRESS(value: string): void {
        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            event.address = new AddressData(-1);
        }

        event.address.street = value;
    }

    @Mutation
    public UPDATE_EVENT_CITY(value: string): void {
        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            event.address = new AddressData(-1);
        }

        event.address.city = value;
    }

    @Mutation
    public UPDATE_EVENT_ZIP_CODE(value: number): void {
        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            event.address = new AddressData(-1);
        }

        event.address.zip = value;
    }

    @Mutation
    public UPDATE_EVENT_COUNTRY(value: number): void {
        const event = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];

        if (event.address === null) {
            event.address = new AddressData(-1);
        }

        event.address.countryId = value;
    }

    @Mutation
    public UPDATE_EVENT_DESCRIPTION(value: string): void {
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].body = value;
    }

    @Mutation
    public EDIT_ACTIVE_EVENT(): void {
        this.cachedEvent = this.calendarEvents[this.calendarEventsMap[this.activeEventId]];
    }

    @Mutation
    public INITIATE_NEW_EVENT() {
        const activeIndex = this.calendarEvents.push(new CalendarEventData(-1)) - 1;
        this.calendarEventsMap[-1] = activeIndex;
        this.activeEventId = -1;
    }

    @Mutation
    public SHOW_EDIT_EVENT_FORM() {
        store.commit(Mutations.SET_CURRENT_FORM, 'edit-event');
        this.editEventFormVisible = true;
    }

    @Mutation
    public HIDE_EDIT_EVENT_FORM() {
        this.editEventFormVisible = false;
    }

    @Mutation
    public UPDATE_CACHED_EVENT(event: EventDataResponse) {
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].title = event.title;
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].publishedAt = event.publishedAt;
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].body = event.body;
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].eventStart = event.startTime;
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].eventEnd = event.endTime;
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].requiresResponse = event.requiresResponse;
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].hasMedia = event.hasMedia;

        if (event.address === null) {
            this.calendarEvents[this.calendarEventsMap[this.activeEventId]].address = null;
        } else {
            this.calendarEvents[this.calendarEventsMap[this.activeEventId]].addAddressData(event.address);
        }

        this.activeEventId = null;
    }

    @Mutation
    public UPDATE_PERSISTED_EVENT(data: any) {
        const currentArrayIndex = this.calendarEventsMap[this.activeEventId];

        this.activeEventId = null;
        this.calendarEventsMap[data.eventId] = currentArrayIndex;
        this.calendarEvents[currentArrayIndex].id = data.eventId;

        if (data.addressId !== null) {
            this.calendarEvents[currentArrayIndex].address.id = data.addressId;
        }

        delete this.calendarEventsMap[-1];
    }

    @Mutation
    public UPDATE_REQUIRES_RESPONSE(value: boolean) {
        this.calendarEvents[this.calendarEventsMap[this.activeEventId]].requiresResponse = value;
    }

    @Mutation
    public SORT_ALL_EVENTS() {
        this.eventsSorted = !this.eventsSorted;

        this.calendarEvents = this.calendarEvents.sort((a, b) => {
            return this.eventsSorted
                ? (new Date(a.eventStart) > new Date(b.eventStart)) ? 1 : -1
                : (new Date(a.eventStart) < new Date(b.eventStart)) ? 1 : -1;
        });

        this.calendarEventsMap = {};

        for (const [i, event] of this.calendarEvents.entries()) {
            this.calendarEventsMap[event.id] = i;
        }
    }

    @Mutation
    public SET_ACTIVE_EVENT_MEDIA(media: any) {
        this.activeEventMedia = media;
    }

    @Mutation
    public RESET_EVENT_ATTENDEES() {
        this.attendees = [];
    }

    @Mutation
    public ADD_EVENT_ATTENDEE(attendee: any) {
        this.attendees.push(new EventAttendees(
            attendee.userId,
            attendee.firstName,
            attendee.familyName,
            attendee.phoneNumber,
            attendee.answer,
        ));
    }

    @Mutation
    public REMOVE_EVENT_ATTENDEE(attendeeId: number) {
        this.attendees = this.attendees.filter((attendee: EventAttendees) => attendee.userId !== attendeeId);
    }

    @Mutation
    public SET_EVENT_ATTENDEES(attendees: any) {
        for (const attendee of attendees) {
            this.attendees.push(new EventAttendees(
                attendee.userId,
                attendee.firstName,
                attendee.familyName,
                attendee.phoneNumber,
                attendee.answer,
            ));
        }
    }
}
